import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser';
import { createLogger } from '@utils/logger';
import { TableFilterFavouritesContext, TableFilterFavouritesContextValue, toViewKey } from './tableFilterFavouritesContext';

const log = createLogger('table-filter-favourites');

const LEGACY_KEY_SUFFIX = '_hearted_fields';
const legacyKeyPrefix = (userId: string) => `user_${userId}_`;

/** Favourites that predate the backend store, still sitting in this browser's localStorage. */
const readLegacyFavourites = (userId: string): Record<string, string[]> => {
  const result: Record<string, string[]> = {};
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(legacyKeyPrefix(userId)) || !key.endsWith(LEGACY_KEY_SUFFIX)) continue;
      const raw = window.localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : null;
      if (!Array.isArray(parsed)) continue;
      result[toViewKey(key.slice(legacyKeyPrefix(userId).length, -LEGACY_KEY_SUFFIX.length))] = parsed.filter(k => typeof k === 'string');
    }
  } catch (error) {
    log.error('could not read legacy favourites from localStorage', error);
  }
  return result;
};

const dropLegacyFavourites = (userId: string) => {
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(legacyKeyPrefix(userId)) && key.endsWith(LEGACY_KEY_SUFFIX)) keys.push(key);
    }
    keys.forEach(key => window.localStorage.removeItem(key));
  } catch (error) {
    log.error('could not clear legacy favourites from localStorage', error);
  }
};

/** How long to coalesce heart clicks before writing. Users toggle several hearts in a row. */
const WRITE_DEBOUNCE_MS = 400;

interface PendingWrite {
  timer: ReturnType<typeof setTimeout>;
  /** What the server is known to hold, so a failed write can be rolled back to it. */
  rollbackTo: string[] | undefined;
}

/**
 * Loads the user's favourite (pinned) filter fields once per session and serves every filter bar
 * from that one map; heart toggles are applied optimistically and written back debounced.
 * See {@link TableFilterFavouritesContextValue} for the three-state semantics.
 */
export const TableFilterFavouritesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getUserTableFilterFavourites, saveUserTableFilterFavourites } = useContext(ApiContext);
  // The context defaults to null outside AuthWrapper (public pages), hence the optional chain.
  const userId = useContext(AuthenticatedUserContext)?.loggedUser?.id;

  const [favourites, setFavouritesState] = useState<Record<string, string[]>>({});
  const [loaded, setLoaded] = useState(false);
  // Guards against StrictMode's double-invoked effects and against several tables mounting at once,
  // both of which would otherwise fire the bootstrap call more than once.
  const inFlightRef = useRef(false);
  const pendingRef = useRef<Record<string, PendingWrite>>({});

  const write = useCallback(
    (viewKey: string, filterKeys: string[], rollbackTo: string[] | undefined) => {
      delete pendingRef.current[viewKey];
      saveUserTableFilterFavourites(viewKey, { filterKeys }, () => undefined, null, {
        // A preference write is not something the user asked to be told about; on failure the chip
        // bar simply snaps back to what the server still holds.
        onError: error => {
          log.error('saving favourites failed — rolling back', { viewKey, error });
          setFavouritesState(prev => {
            const next = { ...prev };
            if (rollbackTo === undefined) delete next[viewKey];
            else next[viewKey] = rollbackTo;
            return next;
          });
        },
      });
    },
    [saveUserTableFilterFavourites],
  );

  const setFavourites = useCallback(
    (viewKey: string, filterKeys: string[]) => {
      const pending = pendingRef.current[viewKey];
      // The rollback target is what the *server* holds, so a burst of toggles rolls back to the last
      // confirmed value rather than to an intermediate optimistic one.
      const rollbackTo = pending ? pending.rollbackTo : favourites[viewKey];
      if (pending) clearTimeout(pending.timer);
      setFavouritesState(prev => ({ ...prev, [viewKey]: filterKeys }));
      pendingRef.current[viewKey] = {
        rollbackTo,
        timer: setTimeout(() => write(viewKey, filterKeys, rollbackTo), WRITE_DEBOUNCE_MS),
      };
    },
    [favourites, write],
  );

  const ensureLoaded = useCallback(() => {
    if (inFlightRef.current || loaded) return;
    // Anonymous / hash-authenticated screens have no user to store preferences for; they keep the
    // FE defaults and any toggles live only for the session.
    if (!userId) return;
    inFlightRef.current = true;
    getUserTableFilterFavourites(
      data => {
        const fromServer: Record<string, string[]> = {};
        (data?.views ?? []).forEach(view => {
          fromServer[toViewKey(view.viewKey)] = view.filterKeys ?? [];
        });

        // One-time migration: favourites this browser stored before the backend existed are pushed
        // up for the views the server doesn't know yet (the server always wins for the rest), then
        // the local keys go away.
        const legacy = readLegacyFavourites(userId);
        Object.entries(legacy).forEach(([viewKey, filterKeys]) => {
          if (viewKey in fromServer) return;
          fromServer[viewKey] = filterKeys;
          write(viewKey, filterKeys, undefined);
        });
        if (Object.keys(legacy).length > 0) dropLegacyFavourites(userId);

        setFavouritesState(fromServer);
        inFlightRef.current = false;
        setLoaded(true);
      },
      null,
      {
        // Without favourites the bar still renders — on the FE defaults — so an error toast would
        // report something the user can neither act on nor even notice.
        onError: error => {
          log.error('favourites unavailable — falling back to the default chip set', error);
          inFlightRef.current = false;
          setLoaded(true);
        },
      },
    );
  }, [getUserTableFilterFavourites, loaded, userId, write]);

  // A reload right after a heart click would otherwise drop the debounced write.
  useEffect(() => {
    const flush = () => {
      Object.entries(pendingRef.current).forEach(([viewKey, pending]) => {
        clearTimeout(pending.timer);
        write(viewKey, favourites[viewKey] ?? [], pending.rollbackTo);
      });
    };
    window.addEventListener('pagehide', flush);
    return () => window.removeEventListener('pagehide', flush);
  }, [favourites, write]);

  const value = useMemo<TableFilterFavouritesContextValue>(
    () => ({
      loaded,
      favouritesFor: viewKey => favourites[viewKey],
      setFavourites,
      ensureLoaded,
    }),
    [loaded, favourites, setFavourites, ensureLoaded],
  );

  return <TableFilterFavouritesContext.Provider value={value}>{children}</TableFilterFavouritesContext.Provider>;
};
